import React, { useEffect, useRef, useState, useCallback } from 'react';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import {getDataWithFetch, getLocalStorage, setLocalStorage, getTagLabelInfo} from "./components/Utils";
import HomePage from './pages/HomePage';
import TopStories from './pages/TopStories';
import AdminPage from './pages/AdminPage';
import ApplicationData from './components/ApplicationData';
import ApplicationContext from './components/ApplicationContext';
import Invest from './pages/Invest';
import FAQ from './pages/FAQ';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';

const App = ({BASE_URL}) => {
	// process.env.PUBLIC_URL = "TEST";
	const obtainedNavItems = useRef(false);
    const [data, setData] = useState(ApplicationData);

	const renderPage = () => {
		switch (data.currentPage) {
			case 'login':
				return <LoginPage setData={setData} />;
			case 'signup':
				return <SignupPage setData={setData} />;
			case 'admin':
				return <AdminPage setData={setData} />;
			case 'topstories':
				return <TopStories setData={setData} />;
			case 'faq':
				return <FAQ setData={setData} />;
			case 'invest':
				return <Invest setData={setData} />;
			default:
				return <HomePage setData={setData} />;
		}
	};

	const [, setChoicePairsList] = useState();

	// Disable this only for temporary release to include all the 
	// new code changes minus monetization.
	const [enableStripe] = useState(ApplicationData.enableStripe);
	const [stripePromise, setStripePromise] = useState(null);

	useEffect(() => {
        (async() => {
			if (obtainedNavItems.current === false) {
				obtainedNavItems.current = true;

				let queryParams = new URLSearchParams(window.location.search);
				ApplicationData.enableAdmin = queryParams.has("enableAdmin");

				// console.log("DEBUG", "App:  ApplicationData.enableAdmin", ApplicationData.enableAdmin);

				// If the query parameter is set to to enableAdmin=true then we want the admin site to load.
				if (ApplicationData.enableAdmin) {
					const username = getLocalStorage("username");
					// console.log("DEBUG", `App:  username = ${username}`);

					if (username === 'admin') {
						setLocalStorage('currentPage', 'admin');
						ApplicationData.currentPage = 'admin';
						setData((prev) => ({ ...prev, currentPage: 'admin' }))
					}
					else {
						setLocalStorage('currentPage', 'login');
						ApplicationData.currentPage = 'login';
						setData((prev) => ({ ...prev, currentPage: 'login' }))
					}
				}
				else {
					// Then only if it was previously set to admin do we want to reset it to home.
					if (getLocalStorage('currentPage') === 'admin' || getLocalStorage('currentPage') === 'login') {
						setLocalStorage('currentPage', 'home');
						ApplicationData.currentPage = 'home';
						setData((prev) => ({ ...prev, currentPage: 'home' }))
					}
				}

				// console.log(`DEBUG:  BASE_URL = ${BASE_URL}`);
				if (BASE_URL) {
                    // console.log('DEBUG', `BASE_URL = `, BASE_URL);

                    let url, res;

					url = BASE_URL + 'index.php/link_data/get_link_data';
					// console.log('DEBUG:', url);
					res = await getDataWithFetch(url);
					ApplicationData.linkData = res;
	
                    url = BASE_URL + "index.php/search_lookup/get_search_list";
					if (ApplicationData.enableAdmin) {
						url += "/false";
					}
					res = await getDataWithFetch(url);
					setChoicePairsList(Object.entries(res));
					ApplicationData.choicePairsList = Object.entries(res);
	
					url = BASE_URL + "index.php/misc/get_intl";
					// console.log('DEBUG:', url);
					res = await getDataWithFetch(url);
					ApplicationData.intl = res;

					// console.log('enableStripe', enableStripe);
					if (enableStripe) {
						// console.log('DEBUG', `BASE_URL = `, BASE_URL);
	
						url = BASE_URL + 'index.php/stripe_util/config';
						// console.log('DEBUG', `url = `, url);
	
						const json = await fetch(url).then((r) => r.json());
						// console.log('DEBUG', `index.php/stripe_util/config -- json = `, json);
	
						const {publishableKey} = json;
						// console.log('DEBUG', `publishableKey = `, publishableKey);
	
						const stripePromise = loadStripe(publishableKey);
						setStripePromise(stripePromise);
					}
				}
			}
        })();

		ApplicationData.BASE_URL = BASE_URL;
	}, [BASE_URL, enableStripe]);

    // Memoized function using useCallback
    ApplicationData.memoizedGetTagLabelInfo = useCallback((input) => {
		// If the cache has not yet beent created then creat it.  First time it gets called if it gets here 
		// first.
		if (!ApplicationData.cache) {
			ApplicationData.cache = new Map();
		}

		if (!ApplicationData.tagLabelLocalised) {
			ApplicationData.tagLabelLocalised = ["To Open", "To Close"];
		}

		// Check if the input exists in the cache
        if (ApplicationData.cache.has(input)) {
			const result = ApplicationData.cache.get(input);
			// console.log('DEBUG', "App - get_region_titles returns cached value:  ", result);
			ApplicationData.tagLabelLocalised = result;
            return;
        }

        // If not in cache, fetch from network
        if (BASE_URL) {
            (async () => {
                // console.log('DEBUG: App - BASE_URL = ', BASE_URL);
                const result = await getTagLabelInfo(BASE_URL, ApplicationData.lang || 'en');
				
                if (result?.status) {
					// console.log('DEBUG', 'App - get_region_titles status = ', result?.status);
					// console.log('DEBUG', `App - get_region_titles result = `, result.result);

					ApplicationData.cache.set(input, result.result);
					ApplicationData.tagLabelLocalised = result.result;
					// console.log('DEBUG: App - tagLabelLocalised', ApplicationData.tagLabelLocalised);
                }
            })();
        }
    }, [BASE_URL]);

	const tagLabelInfoLoaded = useRef(false);
	useEffect(() => {
		if (tagLabelInfoLoaded.current === false) {
			tagLabelInfoLoaded.current = true;
			ApplicationData.lang = getLocalStorage('CURRENT_LANG');
			ApplicationData.memoizedGetTagLabelInfo(ApplicationData.lang);
		}
	}, [BASE_URL]);
	const [authToken, setAuthToken] = useState(getLocalStorage('token'));

	return (
		<ApplicationContext.Provider value={{...ApplicationData, authToken, setAuthToken}}>
			{enableStripe ? (<>
				<Elements stripe={stripePromise}>
					{renderPage()}
				</Elements>
			</>): (<>
				{renderPage()}
			</>)}
		</ApplicationContext.Provider>
	);
};

export default App;
